import { Injectable } from '@angular/core';
import { Router, NavigationExtras } from '@angular/router';
import { NavController } from '@ionic/angular/standalone';

/**
 * Navigation Service
 *
 * Centralizes navigation logic and automatically preserves query parameters
 * throughout the application. This is particularly important for maintaining
 * platform simulation parameters (e.g., ?ionicplatform=android) during development.
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  constructor(
    private router: Router,
    private navCtrl: NavController
  ) {}

  /**
   * Get current query parameters from the browser URL
   */
  private getCurrentQueryParams(): Record<string, string> {
    const params = new URLSearchParams(window.location.search);
    const paramsObj: Record<string, string> = {};
    params.forEach((value, key) => {
      paramsObj[key] = value;
    });
    return paramsObj;
  }

  /**
   * Navigate to a route while preserving current query parameters
   */
  navigate(path: string | string[], extras?: NavigationExtras): Promise<boolean> {
    const queryParams = this.getCurrentQueryParams();

    // Merge existing query params with any provided in extras
    const mergedQueryParams = {
      ...queryParams,
      ...(extras?.queryParams || {})
    };

    // Only add queryParams if there are actual params
    const navigationExtras: NavigationExtras = { ...extras };
    if (Object.keys(mergedQueryParams).length > 0) {
      navigationExtras.queryParams = mergedQueryParams;
    } else {
      delete navigationExtras.queryParams;
    }

    return this.router.navigate(Array.isArray(path) ? path : [path], navigationExtras);
  }

  /**
   * Navigate to root route while preserving current query parameters (Ionic style)
   */
  navigateRoot(path: string): Promise<boolean> {
    const queryParams = this.getCurrentQueryParams();

    // Build URL with query string if params exist
    if (Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString();
      return this.navCtrl.navigateRoot(`${path}?${queryString}`);
    }

    return this.navCtrl.navigateRoot(path);
  }

  /**
   * Navigate forward while preserving current query parameters (Ionic style)
   */
  navigateForward(path: string): Promise<boolean> {
    const queryParams = this.getCurrentQueryParams();

    // Build URL with query string if params exist
    if (Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString();
      return this.navCtrl.navigateForward(`${path}?${queryString}`);
    }

    return this.navCtrl.navigateForward(path);
  }

  /**
   * Navigate back while preserving current query parameters (Ionic style)
   */
  navigateBack(path: string): Promise<boolean> {
    const queryParams = this.getCurrentQueryParams();

    // Build URL with query string if params exist
    if (Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString();
      return this.navCtrl.navigateBack(`${path}?${queryString}`);
    }

    return this.navCtrl.navigateBack(path);
  }

  /**
   * Navigate using Angular router with URL string (for special cases like logout)
   * This also handles direct URL manipulation for cases where Angular's router
   * doesn't properly preserve query parameters
   *
   * This method replicates the exact working logic from the logout redirect
   */
  navigateByUrl(url: string): Promise<boolean> {
    const queryParams = this.getCurrentQueryParams();

    if (Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString();
      const separator = url.includes('?') ? '&' : '?';
      const fullUrl = `${url}${separator}${queryString}`;

      // CRITICAL: Update browser URL first, then navigate
      // This ensures Angular sees the query params in the URL
      window.history.replaceState(null, '', fullUrl);
      return this.router.navigateByUrl(fullUrl);
    }

    return this.router.navigateByUrl(url);
  }

  /**
   * Special navigation method for logout that uses the exact working logic
   * from app.component.ts logout redirect
   */
  navigateForLogout(path: string): Promise<boolean> {
    const queryParams = this.getCurrentQueryParams();

    if (Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString();
      const newUrl = path + '?' + queryString;

      // This is the exact sequence that works for logout redirect
      window.history.replaceState(null, '', newUrl);
      return this.router.navigateByUrl(newUrl, { replaceUrl: true });
    } else {
      return this.router.navigate([path], { replaceUrl: true });
    }
  }
}